import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@tellescope/design-system";
import { snakeCaseToTitleCase } from "@tellescope/utils";
import { EligibilityErrorCard } from "../EligibilityErrorCard";
import type { EligibilityPhase } from "./useEligibilityField";

export interface EligibilityFieldContentProps {
  phase: EligibilityPhase;
  displayStatus?: string;
  payerName?: string;
}

export const EligibilityFieldContent = ({ phase, displayStatus, payerName }: EligibilityFieldContentProps) => {
  const { t } = useTranslation("forms");

  switch (phase) {
    case "idle":
      return null;
    case "loading":
      return (
        <div className="flex flex-col items-center gap-2 py-6">
          <Spinner size="sm" />
          <p className="font-medium text-sm">{t("Checking eligibility...")}</p>
          <p className="text-muted-foreground text-xs">{t("This may take a few moments")}</p>
        </div>
      );
    case "error":
      return <EligibilityErrorCard message={t("Please continue with the form.")} />;
    case "result":
    case "cached": {
      if (!displayStatus) {
        return <EligibilityErrorCard message={t("Please continue with the form.")} />;
      }
      if (displayStatus === "ACTIVE") {
        const displayName = payerName ?? t("Insurance");
        return (
          <div className="border border-green-500 rounded-md p-4 flex gap-3 items-start">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="font-semibold text-sm text-green-700">
              {t("{{name}} eligibility verified", { name: displayName })}
            </p>
          </div>
        );
      }
      return (
        <div className="border border-amber-500 rounded-md p-4 flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="font-semibold text-sm">
            {t("Eligibility Status: {{status}}", { status: snakeCaseToTitleCase(displayStatus) })}
          </p>
        </div>
      );
    }
  }
};
